import React, { useState, useEffect, useRef } from "react";
import ExpandedArea from "../ExpandedArea/ExpandedArea";
import "./ExpandableTableBody.css";

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAnglesUp } from '@fortawesome/free-solid-svg-icons'
import { faAnglesDown } from '@fortawesome/free-solid-svg-icons'
import { faAngleRight } from '@fortawesome/free-solid-svg-icons'
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { faCircleExclamation } from "@fortawesome/free-solid-svg-icons";
import { faCircleQuestion } from "@fortawesome/free-solid-svg-icons";
import {Tooltip} from "react-tooltip";


const ExpandableTableBody = (props) => {
  const [data, setData] = useState([]);
  const [rowItemNames, setRowItemNames] = useState([]);
  const [rowItemInfo, setRowItemInfo] = useState([]);

  const expandedAreaType = props.expandedAreaType;

  const greenUp = (<FontAwesomeIcon icon={faAnglesUp} style={{color: "var(--color-positive)", fontSize: 10}}/>)
  const redDown = (<FontAwesomeIcon icon={faAnglesDown} style={{color: "var(--color-negative)", fontSize: 10}}/>)
  const areaChevron = (<FontAwesomeIcon icon={faAngleRight} className="table-row-chevron" style={{fontSize: 11}}/>)
  const yellowWarning = (<FontAwesomeIcon icon={faTriangleExclamation} style={{color: "var(--color-warn)", fontSize: 14}}
                                          data-tooltip-id="yellow-warning-tooltip"/>)
  const yellowQuestion = (<FontAwesomeIcon icon={faCircleQuestion} style={{color: "var(--color-warn)", fontSize: 14}}
                                           data-tooltip-id="yellow-question-tooltip"/>)
  const orangeQuestion = (<FontAwesomeIcon icon={faCircleQuestion} style={{color: "var(--color-warn-strong)", fontSize: 14}}
                                           data-tooltip-id="orange-question-tooltip"/>)
  const redWarning = (<FontAwesomeIcon icon={faCircleExclamation} style={{color: "var(--color-danger)", fontSize: 14}}
                                       data-tooltip-id="red-warning-tooltip"/>)
  const redWarning2 = (<FontAwesomeIcon icon={faCircleExclamation} style={{color: "var(--color-danger)", fontSize: 14}}
                                        data-tooltip-id="red-warning-tooltip2"/>)

  const toolTips = [
        <Tooltip key="yellow-warning-tooltip" id="yellow-warning-tooltip" className="my-tooltip" content="A doubt for next game due to injury." />,
        <Tooltip key="yellow-question-tooltip" id="yellow-question-tooltip" className="my-tooltip" content="Might not be a starter for this game (<60%)."/>,
        <Tooltip key="orange-question-tooltip" id="orange-question-tooltip" className="my-tooltip" content="Might not be a starter for this game (<10%)."/>,
        <Tooltip key="red-warning-tooltip" id="red-warning-tooltip" className="my-tooltip" content="Unlikely to play — either injured or suspended."/>,
        <Tooltip key="red-warning-tooltip2" id="red-warning-tooltip2" className="my-tooltip" content="Unavailable due to other reason."/>
    ]

  const rowClickHandler = (event) => {
    let isInExpandedArea = ((event.target.classList.value.split("-").includes("expanded")) ||
                            (event.target.parentNode.classList.value.split("-").includes("expanded")) ||
                            (event.target.parentNode.parentNode.classList.value.split("-").includes("expanded")))
    if (isInExpandedArea) {
      return
    }

    let indexVal = parseInt(event.currentTarget.getAttribute("data-index"));
    let isInARow = ((event.target.classList.value.split("-").includes("row")) ||
                    (event.target.parentNode.classList.value.split("-").includes("row")) ||
                    (event.target.parentNode.parentNode.classList.value.split("-").includes("row")))
    if (isInARow) {
        let isActiveRow = (event.currentTarget.classList.value.split(" ").includes("active") ||
                           event.currentTarget.classList.value.split(" ").includes("active-initial"))
        if (isActiveRow) {
            let filteredRows = props.activeRows.filter((val) => val !== indexVal);
            props.setActiveRows(filteredRows);
        } else {
            let newRows = props.activeRows.concat(indexVal);
            props.setActiveRows(newRows);
        }
    }
  };

  const tableBodyRef = useRef(null);
  useEffect(() => {
    if (tableBodyRef.current && !props.alternatingRows) {
      tableBodyRef.current.style.setProperty('--border-between-rows-width', '1px');
      tableBodyRef.current.style.setProperty('--border-between-rows-color', '#E3E3E3');
      tableBodyRef.current.style.setProperty('--border-sides-color', 'white');
      tableBodyRef.current.style.setProperty('--even-row-color', 'white');
    }
  }, [props.alternatingRows]);

  useEffect(() => {
    if (props.data) {
      setData(props.data);
      setRowItemNames(props.rowItemNames);
      setRowItemInfo(props.rowItemInfo);
    }
  }, [props]);

  const alignment = (value) => {
    if (value === "left") {
      return "left-align";
    } else if (value === "right") {
      return "right-align";
    } else {
      return "center-align"
    }
  }

  const icon = (value) => {
    if (value === "greenUp") {
      return greenUp;
    } else if (value === "redDown") {
      return redDown;
    }
  }

  const flag = (value) => {
    if (["s", "i"].includes(value)) {
      return redWarning
    } else if (value === "d") {
      return yellowWarning
    } else if (value === "yellow_doubt") {
      return yellowQuestion
    } else if (value === "orange_doubt") {
      return orangeQuestion
    } else if (value === "n") {
      return redWarning2
    }
  }

  const fontStyle = (value) => {
    if (value === "title") {
      return "font-title";
    } else {
      return "font-number";
    }
  }

  const isEven = (value) => {
    if (value % 2 === 0) {
      return "even";
    } else {
      return "odd";
    }
  };

  const isSortedCol = (item) => {
    if (item === props.clickedHeader) {
      return "sorted";
    }
  };

  const getRowClass = (value) => {
    if (props.activeRows.includes(value)) {
      return "active"
    } else {
      return "inactive"
    }
  }

  // The chevron rotates via CSS when the row container gets the `active` class.
  const areaStatus = () => areaChevron;

  return (
    <div className={"table-body"} ref={tableBodyRef}>
      {data.map((row, rowidx) => {
        const rowValueDictionary = Object.fromEntries(
            Object.entries(row).filter(([key]) => Object.keys(rowItemNames).includes(key))
        );

        return (
          <div key={"row-container-" + rowidx}
               data-index={rowidx}
               className={`table-row-container ${getRowClass(rowidx)} ${isEven(rowidx)}`}
               onClick={rowClickHandler}>
            <div key={"row-" + rowidx} className={`table-row ${isEven(rowidx)}`}>
                {Object.values(rowValueDictionary).map((item, idx) => {
                    let checker = Object.keys(rowValueDictionary)[idx];
                    if (checker === "pred") {
                      item = item.toFixed(2);
                    }

                    if (checker === "price") {
                      item = "£" + item + "m";
                    }

                    const teamLogoCell = (
                        <div key={idx}
                             className={`table-row-item ${alignment(rowItemInfo.align[idx])}`}
                             style={{width: rowItemInfo.width[idx],
                                     minWidth: rowItemInfo.minWidth[idx],
                                     boxSizing: "border-box"}}>
                            <span>
                                <img className="table-row-team-logo"
                                             src={`https://omo.akamai.opta.net/image.php?secure=true&h=omo.akamai.opta.net&sport=football&entity=team&description=badges&dimensions=150&id=${row.team_id}`}
                                             height={17} width={17} alt="" loading="lazy"
                                        />
                            </span>
                        </div>
                    );

                    const itemCell = (
                      <div key={idx}
                           className={`table-row-item
                                      ${alignment(rowItemInfo.align[idx])}
                                      ${fontStyle(rowItemInfo.fontStyle[idx])}
                                      ${isSortedCol(checker)}`}
                           style={{width: rowItemInfo.width[idx],
                                   minWidth: rowItemInfo.minWidth[idx],
                                   boxSizing: "border-box"}}>
                        {idx === 0 && areaStatus(rowidx)} {item} {icon(rowItemInfo.icon[idx])}
                      </div>
                    );

                    const iconCell = (
                        <div key={idx}
                             className={`table-row-item
                                      ${alignment(rowItemInfo.align[idx])}
                                      ${fontStyle(rowItemInfo.fontStyle[idx])}
                                      ${isSortedCol(checker)}`}
                             style={{width: rowItemInfo.width[idx],
                                     minWidth: rowItemInfo.minWidth[idx],
                                     boxSizing: "border-box"}}>
                          {flag(item)}
                        </div>
                    )

                    const valueCell = (
                        <div key={idx}
                             className={`table-row-item table-row-value-cell
                                      ${alignment(rowItemInfo.align[idx])}
                                      ${fontStyle(rowItemInfo.fontStyle[idx])}
                                      ${isSortedCol(checker)}`}
                             style={{width: rowItemInfo.width[idx],
                                     minWidth: rowItemInfo.minWidth[idx],
                                     background: `rgba(23, 89, 128, ${row.alpha})`,
                                     color: row.alpha > 0.65 ? 'white' : 'inherit',
                                     boxSizing: "border-box"
                                     }}>
                          {idx === 0 && areaStatus(rowidx)}{item}{icon(rowItemInfo.icon[idx])}
                        </div>
                    );

                  if (checker === "team_id") {
                    return teamLogoCell;
                  } else if (checker === "pred") {
                    return valueCell;
                  } else if (checker === "status") {
                    return iconCell;
                  } else {
                    return itemCell;
                  }
                })}
            </div>
            <div key={"expander-" + rowidx} className={"table-row-expanded"}>
              {props.activeRows.includes(rowidx) ? (
                  <ExpandedArea
                      data={row}
                      areaType={expandedAreaType}
                  />
              ) : null}
            </div>
          </div>
        );
      })}
      {toolTips.map((row, row_idx) => {
                return row
            })}
    </div>
  );
};

export default ExpandableTableBody;
